import React, { Dispatch, SetStateAction, useState } from 'react';
import { Input } from '@uchi/dino-input';
import { Button } from '@uchi/dino-button';
import classNames from 'classnames';
import useFormLogic from 'components/Modals/AuthModal/hooks/useFormLogic';
import ButtonAsLink from 'components/ButtonAsLink/ButtonAsLink';
import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import loginRequest from 'components/Modals/AuthModal/api/loginRequest';
import { AuthModalProps } from 'components/Modals/AuthModal/AuthModal.types';
import styles from './EntryScreen.module.css';

type Props = {
  setUserInfo: () => void;
  setUnsuccessfulAuth: Dispatch<SetStateAction<boolean>>;
  unsuccessfulAuth: boolean;
  entryPoint: AuthModalProps['entryPoint'];
  version: 'fullscreen' | 'service';
};

const EntryScreen = ({
  setUserInfo,
  setUnsuccessfulAuth,
  unsuccessfulAuth,
  entryPoint,
  version,
}: Props) => {
  const {
    login,
    setLogin,
    password,
    setPassword,
    loginError,
    passwordError,
    validateForm,
    openOtp,
  } = useFormLogic();
  const { sendEvent } = useClickStreamEvent();

  const [disableButton, setDisableButton] = useState(false);
  const [loginErrorReq, setLoginErrorReq] = useState('');

  const sendAuthForm = async () => {
    setLoginErrorReq('');
    if (!validateForm()) {
      return;
    }
    setDisableButton(true);
    const response = await loginRequest(login, password);
    setDisableButton(false);
    if (response.success) {
      await setUserInfo();
      // Удаление параметра auth=true при успешной авторизации
      if (unsuccessfulAuth) {
        await sendEvent({
          eventType: 'click',
          eventInfo: `otvety_auth_after_unsuccessful_${version}`,
          customDimentions: {
            cd1: JSON.stringify({ source: entryPoint }),
          },
        });
      }
      await sendEvent({
        eventType: 'click',
        eventInfo: `otvety_auth_success_${version}`,
        customDimentions: {
          cd1: JSON.stringify({ source: entryPoint }),
        },
      });
    } else if (response.error) {
      setLoginErrorReq(response.error);
      setUnsuccessfulAuth(true);
      sendEvent({
        eventType: 'click',
        eventInfo: `otvety_auth_error_${version}`,
        customDimentions: {
          cd1: JSON.stringify({ source: entryPoint }),
        },
      });
    }
  };

  const handleEnterClick = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      await sendAuthForm();
    }
  };

  const otpBtnHandler = () => {
    sendEvent({
      eventType: 'click',
      eventInfo: `otvety_otp_auth_open_${version}`,
      customDimentions: {
        cd1: JSON.stringify({ source: entryPoint }),
      },
    });
    openOtp(successOtpAuth);
  };

  const successOtpAuth = async () => {
    await sendEvent({
      eventType: 'click',
      eventInfo: `otvety_otp_auth_success_${version}`,
      customDimentions: {
        cd1: JSON.stringify({ source: entryPoint }),
      },
    });
    setUserInfo();
  };

  return (
    <div className={classNames(styles.container, styles[version])}>
      <h3 className={styles.title}>Войди на Учи.ру</h3>
      <div className={styles.inputs}>
        <Input
          id="login"
          value={login}
          onChange={(str) => setLogin(str)}
          placeholder="Логин"
          fluid={true}
          className={styles.input}
          error={!!loginError || !!loginErrorReq || !!passwordError}
          onKeyDown={handleEnterClick}
        />
        <Input
          id="password"
          value={password}
          onChange={(str) => setPassword(str)}
          placeholder="Пароль"
          password={true}
          fluid={true}
          className={styles.input}
          onKeyDown={handleEnterClick}
          error={!!passwordError || !!loginErrorReq || !!loginError}
        />
      </div>
      {(passwordError || loginErrorReq || loginError) && (
        <label htmlFor="" className={styles.label_error}>
          {passwordError || loginErrorReq || loginError}
        </label>
      )}
      <div className={styles.buttons}>
        <Button
          size={48}
          className={styles.button}
          onClick={sendAuthForm}
          disabled={disableButton}
          bgColor={
            loginError || loginErrorReq || passwordError
              ? 'color-system-error-base'
              : 'color-primary-base'
          }
        >
          Войти
        </Button>
        <ButtonAsLink onClick={otpBtnHandler}>Напомнить пароль</ButtonAsLink>
      </div>
    </div>
  );
};

export default EntryScreen;
